import React, { useMemo } from 'react';
import { PROGRAM_NAMES } from '../../../../helpers/constants';
import { usePreviewMode } from '../../../../helpers/hooks/useCheckPreviewMode';
import { useUserProfiles } from '../../../../helpers/hooks/useUserProfiles';
import { UserAvatar } from '../../../../components/UserAvatar';
import plusIcon from '../../../../assets/icons/level/levelMap/plusIcon.svg';

const PLACE_KEYS = {
  [PROGRAM_NAMES.X2]: ['place1', 'place2'],
  [PROGRAM_NAMES.X4]: ['place1', 'place2', 'place3', 'place4', 'place5', 'place6'],
};

const getPlaceColor = (place) => {
  if (!place?.user) {
    return 'empty';
  }

  const userId = place.user?.id?.toLowerCase();
  const realReceiverId = place.realReceiver?.id?.toLowerCase();

  if (realReceiverId && userId && realReceiverId !== userId) {
    return 'overflow';
  }

  return 'filled';
};

const slotStyles = {
  empty: 'bg-white-50 border-dashed border-white-200',
  filled:
    'bg-[rgba(152,90,255,0.16)] border-solid border-[#985AFF] shadow-[0_0_1.6rem_0.2rem_rgba(152,90,255,0.25)_inset]',
  overflow:
    'bg-[rgba(81,246,137,0.10)] border-solid border-[#51F689] shadow-[0_0_1.6rem_0.2rem_rgba(81,246,137,0.20)_inset]',
};

const getCyclePlaces = (cycle, type) => {
  return (PLACE_KEYS[type] || PLACE_KEYS[PROGRAM_NAMES.X2]).map((key) => cycle?.[key] ?? null);
};

const MatrixSlot = ({ place, profile }) => {
  const { setPreviewMode } = usePreviewMode();
  const color = getPlaceColor(place);
  const userId = place?.user?.internalId;
  const userAddress = place?.user?.id;

  const handleClick = () => {
    if (!userAddress) {
      return;
    }

    setPreviewMode(userAddress.toLowerCase(), '/dashboard');
  };

  return (
    <button
      type="button"
      disabled={!userAddress}
      onClick={handleClick}
      className={`relative flex items-center justify-center w-[5.2rem] h-[5.2rem] custom-transition lg:w-[4.4rem] lg:h-[4.4rem] ${
        userAddress ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
      }`}
    >
      <span
        className={`absolute inset-0 overflow-hidden rounded-full border-[0.15rem] ${slotStyles[color]}`}
      >
        {userId ? (
          <UserAvatar
            src={profile?.avatar}
            nickname={profile?.nickname}
            className="h-full w-full text-[1.8rem] lg:text-[1.5rem]"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center">
            <img className="w-[1.2rem] h-[1.2rem] opacity-40 lg:w-[1.1rem] lg:h-[1.1rem]" src={plusIcon} alt="" />
          </span>
        )}
      </span>
      {userId ? (
        <span className="pointer-events-none absolute left-1/2 bottom-0 z-[1] flex h-[1.6rem] min-w-[2rem] -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border-[0.1rem] border-white-100 bg-[#141416] px-[0.5rem] text-[0.9rem] font-500 leading-none text-white lg:h-[1.4rem] lg:text-[0.8rem]">
          {userId}
        </span>
      ) : null}
    </button>
  );
};

const DuoMatrix = ({ places, profiles }) => (
  <div className="grid w-fit grid-cols-2 justify-items-center gap-x-[1.6rem] pb-[1.2rem]">
    <MatrixSlot place={places[0]} profile={profiles[places[0]?.user?.id?.toLowerCase()]} />
    <MatrixSlot place={places[1]} profile={profiles[places[1]?.user?.id?.toLowerCase()]} />
  </div>
);

const QuattroMatrix = ({ places, profiles }) => (
  <div className="grid w-fit grid-cols-4 justify-items-center gap-x-[0.8rem] gap-y-[2rem] pb-[1.2rem]">
    <div className="col-span-2 flex items-center justify-center">
      <MatrixSlot place={places[0]} profile={profiles[places[0]?.user?.id?.toLowerCase()]} />
    </div>
    <div className="col-span-2 flex items-center justify-center">
      <MatrixSlot place={places[1]} profile={profiles[places[1]?.user?.id?.toLowerCase()]} />
    </div>
    <MatrixSlot place={places[2]} profile={profiles[places[2]?.user?.id?.toLowerCase()]} />
    <MatrixSlot place={places[3]} profile={profiles[places[3]?.user?.id?.toLowerCase()]} />
    <MatrixSlot place={places[4]} profile={profiles[places[4]?.user?.id?.toLowerCase()]} />
    <MatrixSlot place={places[5]} profile={profiles[places[5]?.user?.id?.toLowerCase()]} />
  </div>
);

const recycleFromId = (id) => {
  if (!id) {
    return -1;
  }
  const parts = String(id).split('0x');
  return parseInt(parts[parts.length - 1] || '-1', 16);
};

const pickCurrentCycle = (matrixs = [], recyclesTotal) => {
  if (!matrixs.length) {
    return null;
  }
  if (recyclesTotal == null) {
    return matrixs[matrixs.length - 1];
  }
  return matrixs.find((cycle) => recycleFromId(cycle.id) === Number(recyclesTotal)) || matrixs[matrixs.length - 1];
};

export const getDisplayPlaces = (matrixs = [], type, useNextRecycle = true, recyclesTotal = null) => {
  const keys = PLACE_KEYS[type] || PLACE_KEYS[PROGRAM_NAMES.X2];
  const lastCyclePlaces = getCyclePlaces(pickCurrentCycle(matrixs, recyclesTotal), type);
  const isCycleFull = lastCyclePlaces.length > 0 && lastCyclePlaces.every((place) => !!place?.user);

  if (!matrixs?.length || (useNextRecycle && isCycleFull)) {
    return keys.map(() => null);
  }

  return lastCyclePlaces;
};

export const MatrixPlaces = ({ type, matrixs = [], places: placesProp, useNextRecycle = true, recyclesTotal = null }) => {
  const places = useMemo(() => {
    if (placesProp) {
      return placesProp;
    }

    return getDisplayPlaces(matrixs, type, useNextRecycle, recyclesTotal);
  }, [matrixs, placesProp, type, useNextRecycle, recyclesTotal]);

  const addresses = useMemo(() => places.map((place) => place?.user?.id).filter(Boolean), [places]);
  const profiles = useUserProfiles(addresses);

  return type === PROGRAM_NAMES.X2 ? (
    <DuoMatrix places={places} profiles={profiles} />
  ) : (
    <QuattroMatrix places={places} profiles={profiles} />
  );
};
